// contexts/ThemeContext.tsx
// UI Tier 3.2: skin lineup reduced to System / Light / Dark ("Appearance" in
// Settings). The retired Retro and Studio skins resolve to Light — Studio
// became measurably redundant once the warm design language landed (ground
// colors within 14.5 RGB units), and Retro was an unused novelty that fought
// institution brand colors. Stored preferences with retired values degrade
// gracefully via normalizeTheme.
import { useState, useEffect, type ReactNode } from 'react';
import { createSafeContext } from '../utils/createSafeContext';

// =============================================================================
// TYPES
// =============================================================================

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Skins retired 2026-07-09; stored values map to their nearest survivor. */
const RETIRED_THEME_MAP: Record<string, Theme> = {
  retro: 'light',
  studio: 'light',
};

export const normalizeTheme = (value: string | null | undefined): Theme => {
  if (value === 'light' || value === 'dark' || value === 'system') return value;
  if (value && value in RETIRED_THEME_MAP) return RETIRED_THEME_MAP[value];
  return 'light';
};

export interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

// =============================================================================
// CONTEXT
// =============================================================================

const [ThemeContext, useTheme] = createSafeContext<ThemeContextValue>('ThemeContext');
export { useTheme };

// =============================================================================
// PROVIDER
// =============================================================================

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Initialize from localStorage (normalizing retired skins) or default to 'light'
  const [theme, setThemeState] = useState<Theme>(() =>
    normalizeTheme(localStorage.getItem('theme'))
  );

  // Get the actual resolved theme (for 'system' mode)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const updateResolvedTheme = () => {
      if (theme === 'system') {
        setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
      } else {
        setResolvedTheme(theme);
      }
    };

    updateResolvedTheme();
    mediaQuery.addEventListener('change', updateResolvedTheme);

    return () => mediaQuery.removeEventListener('change', updateResolvedTheme);
  }, [theme]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    // Remove existing theme classes (retired classes cleared for stale DOM state)
    root.classList.remove('light', 'dark', 'retro', 'studio');

    // Add current theme class
    root.classList.add(resolvedTheme);

    // Also set a data attribute for CSS selectors
    root.setAttribute('data-theme', resolvedTheme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const themeColors: Record<ResolvedTheme, string> = {
        dark: '#161412',
        light: '#F5F1EB',
      };
      metaThemeColor.setAttribute('content', themeColors[resolvedTheme] || '#F5F1EB');
    }
  }, [resolvedTheme]);

  const setTheme = (newTheme: Theme) => {
    const normalized = normalizeTheme(newTheme);
    setThemeState(normalized);
    localStorage.setItem('theme', normalized);
  };

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme,
    isDark: resolvedTheme === 'dark',
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
